import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, getAccessToken, getWsUrl } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Send, MessageCircle, Users, Search, X, ArrowLeft } from 'lucide-react';

// Helper function to format time ago
function formatTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function ChatPage({ isAdmin = false }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);

  const fetchRooms = useCallback(async () => {
    try {
      const data = await api('/api/chat/rooms');
      setRooms(data.rooms);
      setLoading(false);
    } catch (err) { console.error(err); setLoading(false); }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // Filter and sort rooms
  const filteredAndSortedRooms = React.useMemo(() => {
    let filtered = rooms;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = rooms.filter(room =>
        room.team_name?.toLowerCase().includes(query) ||
        room.team_id?.toLowerCase().includes(query) ||
        room.mentor_name?.toLowerCase().includes(query) ||
        room.mentor_email?.toLowerCase().includes(query)
      );
    }

    // Sort by last message timestamp (most recent first)
    return filtered.sort((a, b) => {
      const aTime = a.last_message?.created_at || a.created_at || '';
      const bTime = b.last_message?.created_at || b.created_at || '';

      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;  // Rooms without messages go to bottom
      if (!bTime) return -1;

      return new Date(bTime) - new Date(aTime);  // Descending order
    });
  }, [rooms, searchQuery]);

  const fetchMessages = async (roomId) => {
    try {
      const data = await api(`/api/chat/messages/${roomId}`);
      setMessages(data.messages);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error(err); }
  };

  const connectWebSocket = (roomId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    const token = getAccessToken();
    const wsUrl = getWsUrl(`/api/ws/chat/${roomId}?token=${token}`);
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    };
    ws.onerror = () => {};
    ws.onclose = () => {};
    wsRef.current = ws;
  };

  const selectRoom = (room) => {
    setActiveRoom(room);
    fetchMessages(room._id);
    connectWebSocket(room._id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeRoom) return;
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content: newMessage }));
    } else {
      await api('/api/chat/messages', {
        method: 'POST',
        body: JSON.stringify({ room_id: activeRoom._id, content: newMessage }),
      });
      fetchMessages(activeRoom._id);
    }
    setNewMessage('');
  };

  useEffect(() => {
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight" style={{fontFamily:'Space Grotesk'}}>
          {isAdmin ? 'Chat Monitor' : 'Chat'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isAdmin ? 'Monitor and join team-mentor conversations' : 'Communicate with your assigned contacts'}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] overflow-hidden">
        {/* Rooms List */}
        <Card className={`border-0 shadow-sm lg:col-span-1 flex flex-col overflow-hidden ${activeRoom ? 'hidden lg:flex' : 'flex'}`}>
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-sm">Conversations ({rooms.length})</CardTitle>

            {/* Search Input */}
            {isAdmin && rooms.length > 0 && (
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search team or mentor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9 h-9"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            <ScrollArea className="h-full" data-testid="chat-conversation-list">
              {loading ? (
                <div className="p-4 space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 skeleton-shimmer rounded-lg" />)}</div>
              ) : filteredAndSortedRooms.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ? 'No conversations found' : 'No conversations yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-0.5 p-2">
                  {filteredAndSortedRooms.map((room) => {
                    const lastMsgTime = room.last_message?.created_at;
                    const timeAgo = lastMsgTime ? formatTimeAgo(new Date(lastMsgTime)) : null;

                    return (
                      <div
                        key={room._id}
                        onClick={() => selectRoom(room)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${activeRoom?._id === room._id ? 'bg-accent' : 'hover:bg-accent/50'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium truncate flex-1">{room.team_name || room.team_id}</p>
                          {timeAgo && (
                            <span className="text-[10px] text-muted-foreground ml-2 shrink-0">{timeAgo}</span>
                          )}
                          {room.unread_count > 0 && <Badge className="text-xs h-5 min-w-[20px] ml-2">{room.unread_count}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{room.mentor_name || room.mentor_email}</p>
                        {room.last_message && (
                          <p className="text-xs text-muted-foreground truncate mt-1 opacity-75">
                            {room.last_message.content}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Results Counter */}
            {isAdmin && searchQuery && filteredAndSortedRooms.length > 0 && (
              <div className="p-2 border-t text-xs text-muted-foreground text-center">
                Showing {filteredAndSortedRooms.length} of {rooms.length}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Thread */}
        <Card className={`border-0 shadow-sm lg:col-span-2 flex flex-col overflow-hidden ${!activeRoom ? 'hidden lg:flex' : 'flex'}`}>
          {activeRoom ? (
            <>
              <CardHeader className="pb-3 border-b flex-shrink-0">
                <div className="flex items-center gap-3">
                  {/* Back button for mobile */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveRoom(null)}
                    className="lg:hidden p-2 h-8 w-8"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>

                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm truncate">{activeRoom.team_name || activeRoom.team_id}</CardTitle>
                    <p className="text-xs text-muted-foreground truncate">{activeRoom.mentor_email}</p>
                  </div>
                  {isAdmin && (
                    <Badge variant="secondary" className="hidden sm:inline-flex" data-testid="chat-join-button">Admin</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 flex flex-col min-h-0">
                <ScrollArea className="flex-1 p-4 overflow-auto" ref={scrollAreaRef} data-testid="chat-thread">
                  <div className="space-y-3">
                    {messages.map((msg, i) => {
                      const isMine = msg.sender_email === user?.email;
                      return (
                        <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`chat-message-bubble ${isMine ? 'chat-message-sent' : 'chat-message-received'}`}>
                            {!isMine && (
                              <p className="text-xs font-medium mb-1 opacity-70">
                                {msg.sender_name} {msg.sender_role === 'admin' ? '(Admin)' : ''}
                              </p>
                            )}
                            <p>{msg.content}</p>
                            <p className="text-xs mt-1 opacity-50">
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-3 border-t flex gap-2 flex-shrink-0 bg-background">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    data-testid="chat-message-input"
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} size="icon" className="btn-press flex-shrink-0" data-testid="chat-send-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Select a conversation to start chatting</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
